/**
 * Shipping Calculator
 * Calcula frete baseado em produtos do carrinho, buy-more ou quantity input
 * @version 2.0.0
 */

(function() {
  'use strict';

  const CONFIG = {
    SELECTORS: {
      INPUT: '[data-shipping-calculator-input]',
      BUTTON: '[data-shipping-calculator-button]',
      MESSAGE: '.shipping-message',
      BUY_MORE_BLOCK: 'buy-more-block',
      BUY_MORE_RADIO: 'input[type="radio"]',
      BUY_MORE_DYNAMIC_INPUT: 'input[data-buy-more-quantity-input]',
      BUY_MORE_BUNDLE: '[data-buy-more-bundle]',
      BUY_MORE_BUNDLE_ITEM: '[data-bundle-item]',
      BUY_MORE_BUNDLE_QTY: '[data-buy-more-qty]',
      BUY_MORE_OPTION: '.buy-more-option',
      PRODUCT_FORM: 'product-form',
      VARIANT_INPUT: 'input[name="id"], select[name="id"]',
      VARIANT_SELECTORS: 'variant-radios, variant-selects',
      QUANTITY_INPUT: 'input[name="quantity"]:not([data-buy-more-quantity-input]):not([data-buy-more-qty])',
      COMPLEMENTARY: '.complementary-products',
      RECOMMENDATIONS: 'product-recommendations',
      CARD_WRAPPER: '.card-wrapper'
    },
    
    API: {
      CART: '/cart.js',
      CART_ADD: '/cart/add.js',
      CART_CLEAR: '/cart/clear.js',
      SHIPPING_RATES: '/cart/shipping_rates.json',
      VIACEP: 'https://viacep.com.br/ws/'
    },
    
    STORAGE_KEY: 'quero_chuva_shipping_cep',
    
    TIMING: {
      RETRY_DELAY: 200,
      GIFT_WAIT: 300,
      INIT_RETRY: 500
    },
    
    LIMITS: {
      MAX_CART_RETRIES: 15,
      CEP_LENGTH: 8
    },
    
    MESSAGES: {
      INVALID_CEP: 'Por favor, insira um CEP válido com 8 dígitos.',
      NO_VARIANT: 'Por favor, selecione uma variante do produto.',
      NO_PRODUCT_DATA: 'Erro: Dados do produto não encontrados.',
      NO_ITEMS: 'Nenhum item válido para calcular o frete.',
      NO_RATES: 'Nenhuma opção de frete encontrada para este CEP.',
      ERROR: 'Erro ao calcular frete. Por favor, tente novamente.',
      CALCULATING: 'Calculando frete...',
      BUTTON_TEXT: 'Calcular frete',
      PLACEHOLDER: 'Digite um CEP e clique no botão para calcular o frete'
    }
  };

  const Debug = {
    enabled: false,
    prefix: '[Shipping Calculator]',

    log: function(...args) {
      if (!this.enabled) return;
      console.log(this.prefix, ...args);
    },

    warn: function(...args) {
      if (!this.enabled) return;
      console.warn(this.prefix, ...args);
    },

    error: function(...args) {
      console.error(this.prefix, ...args);
    },

    success: function(message, ...args) {
      if (!this.enabled) return;
      console.log(`%c${this.prefix} ✓ ${message}`, 'color: #4CAF50; font-weight: bold;', ...args);
    },

    header: function(message) {
      if (!this.enabled) return;
      console.log(`%c${this.prefix} ${message}`, 'color: #9C27B0; font-weight: bold; font-size: 14px;');
    },

    section: function(message, color = '#2196F3') {
      if (!this.enabled) return;
      console.log(`%c${this.prefix} ${message}`, `color: ${color}; font-weight: bold;`);
    },

    group: function(label) {
      if (!this.enabled) return;
      console.group(`${this.prefix} ${label}`);
    },

    groupEnd: function() {
      if (!this.enabled) return;
      console.groupEnd();
    }
  };

  const Utils = {
    parseQuantity: function(value) {
      const qty = parseInt(value, 10);
      return !isNaN(qty) && qty > 0 ? qty : 0;
    },

    formatCEP: function(value) {
      const clean = value.replace(/\D/g, '');
      return clean.length > 5 ? clean.replace(/(\d{5})(\d)/, '$1-$2') : clean;
    },

    cleanCEP: function(value) {
      return value.replace(/\D/g, '');
    },

    isValidCEP: function(value) {
      return value.length === CONFIG.LIMITS.CEP_LENGTH;
    },

    getCSSVariable: function(name) {
      return getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim()
        .split(',')
        .map(v => v.trim())
        .join(', ');
    }
  };

  const DOM = {
    findMainProductForm: function() {
      const forms = document.querySelectorAll(CONFIG.SELECTORS.PRODUCT_FORM);
      
      for (let form of forms) {
        if (this.isAuxiliaryElement(form)) continue;
        
        const variantInput = form.querySelector(CONFIG.SELECTORS.VARIANT_INPUT);
        if (variantInput?.value) return variantInput.value;
      }
      
      return null;
    },

    findBundleFirstItem: function() {
      const buyMore = document.querySelector(CONFIG.SELECTORS.BUY_MORE_BLOCK);
      const firstItem = buyMore?.querySelector(CONFIG.SELECTORS.BUY_MORE_BUNDLE_ITEM);
      return firstItem?.dataset.variantId || null;
    },

    findAnyProductVariant: function() {
      const inputs = document.querySelectorAll('.product-variant-id');
      
      for (let input of inputs) {
        if (this.isAuxiliaryElement(input)) continue;
        if (input.value) return input.value;
      }
      
      return null;
    },

    isAuxiliaryElement: function(element) {
      return element.closest(CONFIG.SELECTORS.COMPLEMENTARY) ||
             element.closest(CONFIG.SELECTORS.RECOMMENDATIONS) ||
             element.closest(CONFIG.SELECTORS.CARD_WRAPPER);
    },

    getSectionId: function(input) {
      const match = input.id?.match(/shipping-calculator-input-(.+)/);
      return match ? match[1] : '';
    }
  };

  const BuyMore = {
    getBlock: function() {
      return document.querySelector(CONFIG.SELECTORS.BUY_MORE_BLOCK);
    },

    getSelectedOption: function() {
      const block = this.getBlock();
      return block?.querySelector(`${CONFIG.SELECTORS.BUY_MORE_RADIO}:checked`);
    },

    isOptionDynamic: function(option) {
      const container = option?.closest(CONFIG.SELECTORS.BUY_MORE_OPTION);
      return container?.hasAttribute('data-dynamic') || false;
    },

    getQuantity: function() {
      const option = this.getSelectedOption();
      if (!option) return null;

      const container = option.closest(CONFIG.SELECTORS.BUY_MORE_OPTION);
      
      if (this.isOptionDynamic(option)) {
        const input = container?.querySelector(CONFIG.SELECTORS.BUY_MORE_DYNAMIC_INPUT);
        return Utils.parseQuantity(input?.value) || null;
      }
      
      return Utils.parseQuantity(option.value) || null;
    },

    getBundleItems: function() {
      const block = this.getBlock();
      const bundle = block?.querySelector(CONFIG.SELECTORS.BUY_MORE_BUNDLE);
      
      if (!bundle) return [];

      const items = [];
      const bundleItems = bundle.querySelectorAll(CONFIG.SELECTORS.BUY_MORE_BUNDLE_ITEM);

      bundleItems.forEach(item => {
        const qtyInput = item.querySelector(CONFIG.SELECTORS.BUY_MORE_BUNDLE_QTY);
        if (!qtyInput || qtyInput.disabled) return;

        const quantity = Utils.parseQuantity(qtyInput.value);
        if (quantity === 0) return;

        const variantId = Utils.parseQuantity(item.dataset.variantId);
        if (variantId === 0) return;

        items.push({ id: variantId, quantity });
      });

      return items;
    },

    hasBundle: function() {
      const block = this.getBlock();
      return !!block?.querySelector(CONFIG.SELECTORS.BUY_MORE_BUNDLE);
    }
  };

  const Product = {
    getVariantId: function() {
      return DOM.findMainProductForm() || 
             DOM.findBundleFirstItem() || 
             DOM.findAnyProductVariant();
    },

    getData: function(sectionId) {
      try {
        return this._fromScriptTag(sectionId) ||
               this._fromWindow() ||
               this._fromVariantScript() ||
               this._fromShopify() ||
               null;
      } catch (e) {
        Debug.error('Erro ao obter dados do produto:', e);
        return null;
      }
    },

    _fromScriptTag: function(sectionId) {
      const script = document.querySelector(`script[data-product-json-${sectionId}]`) ||
                     document.querySelector('script[type="application/json"][data-product-json]');
      
      return script ? JSON.parse(script.textContent) : null;
    },

    _fromWindow: function() {
      return window.product || null;
    },

    _fromVariantScript: function() {
      const script = document.querySelector('textarea[data-variant-json]');
      if (!script) return null;

      const variants = JSON.parse(script.textContent);
      const variantId = this.getVariantId();
      
      return variants.find(v => v.id == variantId) || null;
    },

    _fromShopify: function() {
      return (typeof Shopify !== 'undefined' && Shopify.theme?.product) || null;
    }
  };

  const Quantity = {
    getStandardInput: function() {
      return document.querySelector(`quantity-input ${CONFIG.SELECTORS.QUANTITY_INPUT}`) ||
             document.querySelector(CONFIG.SELECTORS.QUANTITY_INPUT) ||
             document.querySelector(`.quantity__input:not([data-buy-more-quantity-input]):not([data-buy-more-qty])`);
    },

    getStandardQuantity: function() {
      const input = this.getStandardInput();
      return Utils.parseQuantity(input?.value) || 1;
    },

    getCurrent: function() {
      const buyMoreQty = BuyMore.getQuantity();
      return buyMoreQty || this.getStandardQuantity();
    }
  };

  const CartItems = {
    prepare: function(variantId) {
      Debug.header('===== MONTANDO ITENS PARA O CARRINHO =====');

      const bundleItems = BuyMore.getBundleItems();
      
      if (bundleItems.length > 0) {
        Debug.success(`Bundle com ${bundleItems.length} item(ns) selecionado(s)`);
        return bundleItems;
      }

      if (BuyMore.hasBundle()) {
        Debug.log('Bundle vazio - usando produto da página');
        const qty = BuyMore.getQuantity();
        const pageVariantId = DOM.findBundleFirstItem() || variantId;
        
        if (pageVariantId && qty) {
          Debug.success(`Produto da página (bundle vazio): variant_id=${pageVariantId}, qty=${qty}`);
          return [{ id: parseInt(pageVariantId), quantity: qty }];
        }
      }

      const buyMoreQty = BuyMore.getQuantity();
      if (buyMoreQty && variantId) {
        Debug.success(`Produto da página com qty buy-more: variant_id=${variantId}, qty=${buyMoreQty}`);
        return [{ id: parseInt(variantId), quantity: buyMoreQty }];
      }

      const standardQty = Quantity.getStandardQuantity();
      Debug.success(`Produto da página: variant_id=${variantId}, qty=${standardQty}`);
      return [{ id: parseInt(variantId), quantity: standardQty }];
    }
  };

  const Cart = {
    state: null,

    save: function() {
      Debug.log('Salvando estado original do carrinho...');
      
      return fetch(CONFIG.API.CART)
        .then(res => res.json())
        .then(cart => {
          this.state = cart;
          Debug.log(`Estado salvo: ${cart.items?.length || 0} itens`);
          return cart;
        })
        .catch(err => {
          Debug.error('Erro ao salvar estado:', err);
          return null;
        });
    },

    restore: function() {
      if (!this.state) {
        Debug.log('Nenhum estado para restaurar');
        return Promise.resolve();
      }

      Debug.log(`Restaurando ${this.state.items?.length || 0} itens...`);

      return fetch(CONFIG.API.CART_CLEAR, { method: 'POST' })
        .then(() => {
          if (!this.state.items?.length) return;

          const promises = this.state.items.map(item =>
            fetch(CONFIG.API.CART_ADD, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: item.variant_id,
                quantity: item.quantity,
                properties: item.properties
              })
            }).catch(err => Debug.error('Erro ao restaurar item:', err))
          );

          return Promise.all(promises);
        })
        .then(() => {
          Debug.success('Carrinho restaurado com sucesso!');
          this.state = null;
        })
        .catch(err => Debug.error('Erro ao restaurar carrinho:', err));
    },

    add: function(items) {
      const payload = {
        items: items.map(item => ({
          id: parseInt(item.id),
          quantity: parseInt(item.quantity),
          properties: { '_shipping_calc': 'true' }
        }))
      };

      Debug.section('ADICIONANDO AO CARRINHO:', '#E91E63');
      Debug.log('Payload:', JSON.stringify(payload, null, 2));

      return fetch(CONFIG.API.CART_ADD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'xmlhttprequest'
        },
        body: JSON.stringify(payload)
      }).then(res => {
        Debug.log(`Resposta: ${res.status} ${res.statusText}`);
        return res.json();
      });
    },

    waitForItems: function(expectedItems, attempt = 0, maxAttempts = CONFIG.LIMITS.MAX_CART_RETRIES) {
      if (attempt === 0) {
        Debug.log('Aguardando itens no carrinho...');
      }

      if (attempt >= maxAttempts) {
        Debug.warn(`Timeout após ${maxAttempts} tentativas`);
        return Promise.resolve();
      }

      return fetch(CONFIG.API.CART)
        .then(res => res.json())
        .then(cart => {
          if (!cart.items?.length) {
            if (attempt % 3 === 0) {
              Debug.log(`Carrinho vazio... tentativa ${attempt + 1}/${maxAttempts}`);
            }
            return this._retry(() => this.waitForItems(expectedItems, attempt + 1, maxAttempts));
          }

          const allPresent = expectedItems.every(exp =>
            cart.items.some(item => item.variant_id == exp.id)
          );

          if (allPresent) {
            Debug.success('Todos os itens no carrinho!');
            Debug.log(`Total: ${cart.items.length} (incluindo possíveis brindes)`);
            return this._wait(CONFIG.TIMING.GIFT_WAIT);
          }

          if (attempt % 3 === 0) {
            Debug.log(`Aguardando... tentativa ${attempt + 1}/${maxAttempts}`);
          }
          return this._retry(() => this.waitForItems(expectedItems, attempt + 1, maxAttempts));
        })
        .catch(err => {
          Debug.error('Erro ao verificar carrinho:', err);
          return this._retry(() => this.waitForItems(expectedItems, attempt + 1, maxAttempts));
        });
    },

    inspect: function() {
      return fetch(CONFIG.API.CART)
        .then(res => res.json())
        .then(cart => {
          Debug.section('===== CARRINHO FINAL =====', '#673AB7');
          Debug.log(`Total de itens: ${cart.item_count}`);
          Debug.log(`Produtos distintos: ${cart.items?.length || 0}`);
          Debug.log(`Peso total: ${cart.total_weight}g`);

          cart.items?.forEach((item, idx) => {
            const isFree = item.final_price === 0 || item.price === 0;
            const isCalc = item.properties?._shipping_calc === 'true';
            const marker = isFree && !isCalc ? '🎁 BRINDE' : '📦 PRODUTO';

            Debug.group(`[${idx}] ${marker}`);
            Debug.log('title:', item.product_title || item.title);
            Debug.log('variant_id:', item.variant_id);
            Debug.log('quantity:', item.quantity);
            Debug.log('price:', item.price);
            Debug.log('grams:', item.grams);
            Debug.groupEnd();
          });

          return cart;
        })
        .catch(err => Debug.error('Erro ao inspecionar carrinho:', err));
    },

    _wait: function(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    _retry: function(fn) {
      return new Promise(resolve => setTimeout(() => fn().then(resolve), CONFIG.TIMING.RETRY_DELAY));
    }
  };

  const Address = {
    getFromCEP: function(cep) {
      return fetch(`${CONFIG.API.VIACEP}${cep}/json/`)
        .then(res => res.json())
        .then(data => {
          if (data.erro) throw new Error('CEP não encontrado');
          
          return {
            street: data.logradouro || 'Rua',
            neighborhood: data.bairro || 'Centro',
            city: data.localidade || 'São Paulo',
            state: data.uf || 'SP'
          };
        })
        .catch(() => ({
          street: 'Rua',
          neighborhood: 'Centro',
          city: 'São Paulo',
          state: 'SP'
        }));
    }
  };

  const Shipping = {
    calculate: function(cep, items) {
      let addressData;

      Debug.section('Preparando requisição de frete...', '#FF5722');
      Debug.log('CEP:', cep);
      Debug.log('Itens:', items);

      return Promise.all([Cart.save(), Address.getFromCEP(cep)])
        .then(([_, address]) => {
          addressData = address;
          Debug.log('Endereço:', address);
          return fetch(CONFIG.API.CART_CLEAR, { method: 'POST' });
        })
        .then(() => Cart.add(items))
        .then(() => Cart.waitForItems(items))
        .then(() => Cart.inspect())
        .then(() => this._getRates(cep, addressData))
        .then(result => Cart.restore().then(() => result))
        .catch(err => {
          Debug.error('Erro no cálculo:', err);
          return Cart.restore().then(() => Promise.reject(err));
        });
    },

    _getRates: function(cep, address) {
      const params = new URLSearchParams({
        'shipping_address[country]': 'Brazil',
        'shipping_address[province]': address.state,
        'shipping_address[zip]': cep,
        'shipping_address[city]': address.city,
        'shipping_address[address1]': address.street,
        'shipping_address[address2]': address.neighborhood
      });

      const url = `${CONFIG.API.SHIPPING_RATES}?${params}`;

      Debug.section('BUSCANDO TAXAS DE FRETE:', '#00BCD4');
      Debug.log('URL:', url);

      return fetch(url)
        .then(res => {
          Debug.log(`Resposta: ${res.status} ${res.statusText}`);
          return res.json();
        })
        .then(result => {
          Debug.log('Taxas recebidas:', result);
          return result;
        });
    }
  };

  const UI = {
    elements: {},

    init: function(input, button, message) {
      this.elements = { input, button, message };
    },

    showError: function(message) {
      this.elements.message.innerHTML = `<div style="color: #E91E63;">${message}</div>`;
    },

    showCalculating: function() {
      this.elements.button.innerHTML = CONFIG.MESSAGES.CALCULATING;
      this.elements.button.disabled = true;
      this.showError(CONFIG.MESSAGES.CALCULATING);
    },

    resetButton: function() {
      this.elements.button.innerHTML = CONFIG.MESSAGES.BUTTON_TEXT;
      this.elements.button.disabled = false;
    },

    showQuantityChanged: function() {
      const html = this.elements.message.innerHTML;
      
      if (html.includes('Digite um CEP') || html.includes('Calculando')) return;
      
      const hasResults = html.includes('R$') || html.includes('GRÁTIS') || html.includes('útil');
      
      if (hasResults) {
        const qty = Quantity.getCurrent();
        this.elements.message.innerHTML = `
          <div style="color: #FF9800; font-size: 13px; padding: 10px; background: rgba(255, 152, 0, 0.1); border-radius: 6px; border-left: 3px solid #FF9800;">
            <strong>⚠️ Quantidade alterada para ${qty} un.</strong><br>
            <span style="font-size: 12px;">Clique em "Calcular frete" para recalcular.</span>
          </div>
        `;
        Debug.log(`Mensagem resetada - nova quantidade: ${qty}`);
      }
    },

    displayRates: function(rates) {
      const buttonRGB = Utils.getCSSVariable('--color-button');
      const foregroundRGB = Utils.getCSSVariable('--color-foreground');
      const backgroundRGB = Utils.getCSSVariable('--color-background');

      let html = `<div style="background: rgba(${backgroundRGB}, 0.5); border: 1px solid rgba(${foregroundRGB}, 0.1); border-radius: 6px; padding: 15px; margin-top: 10px;">`;

      rates.forEach((rate, i) => {
        const isLast = i === rates.length - 1;
        const border = isLast ? '' : `border-bottom: 1px solid rgba(${foregroundRGB}, 0.1);`;
        
        let deliveryInfo = rate.description;
        if (!deliveryInfo && rate.delivery_days?.length) {
          deliveryInfo = rate.delivery_days[0] == 1 ? '1 dia útil' : `${rate.delivery_days[0]} dias úteis`;
        }

        const price = parseFloat(rate.price);
        const deliveryPrice = price === 0 
          ? `<span style="color: rgb(${buttonRGB}); font-weight: 700;">GRÁTIS</span>`
          : `R$ ${rate.price.replace('.', ',')}`;

        html += `
          <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; ${border}">
            <div style="display: flex; align-items: center;">
              <span style="font-weight: 500; color: rgb(${foregroundRGB});">${rate.name || 'Frete'}</span>
              ${deliveryInfo ? `<span style="margin-left: 8px; color: rgba(${foregroundRGB}, 0.6); font-size: 13px;">(${deliveryInfo})</span>` : ''}
            </div>
            <span style="font-weight: ${price === 0 ? 700 : 600}; color: rgb(${buttonRGB});">${deliveryPrice}</span>
          </div>
        `;
      });

      html += '</div>';
      this.elements.message.innerHTML = html;
    }
  };

  const Storage = {
    saveCEP: function(cep) {
      const clean = Utils.cleanCEP(cep);
      if (Utils.isValidCEP(clean)) {
        localStorage.setItem(CONFIG.STORAGE_KEY, clean);
      }
    },

    loadCEP: function() {
      const saved = localStorage.getItem(CONFIG.STORAGE_KEY);
      return saved && Utils.isValidCEP(saved) ? Utils.formatCEP(saved) : '';
    }
  };

  const EventHandlers = {
    onCalculateClick: function(e) {
      e.preventDefault();
      e.stopPropagation();

      const cep = Utils.cleanCEP(UI.elements.input.value);

      if (!Utils.isValidCEP(cep)) {
        UI.showError(CONFIG.MESSAGES.INVALID_CEP);
        return;
      }

      const variantId = Product.getVariantId();
      Debug.log('Variant ID:', variantId);

      if (!variantId) {
        UI.showError(CONFIG.MESSAGES.NO_VARIANT);
        return;
      }

      UI.showCalculating();

      const productData = Product.getData(DOM.getSectionId(UI.elements.input));
      if (!productData) {
        UI.showError(CONFIG.MESSAGES.NO_PRODUCT_DATA);
        UI.resetButton();
        return;
      }

      Storage.saveCEP(cep);

      const cartItems = CartItems.prepare(variantId);
      if (!cartItems.length) {
        UI.showError(CONFIG.MESSAGES.NO_ITEMS);
        UI.resetButton();
        return;
      }

      Debug.header('===== INICIANDO CÁLCULO DE FRETE =====');
      Debug.log('CEP:', cep);
      Debug.log('Itens:', cartItems);
      Debug.log('Total de unidades:', cartItems.reduce((sum, item) => sum + item.quantity, 0));

      Shipping.calculate(cep, cartItems)
        .then(result => {
          if (result.shipping_rates?.length) {
            Debug.success('Frete calculado com sucesso!');
            Debug.log('Opções:', result.shipping_rates);
            UI.displayRates(result.shipping_rates);
          } else {
            Debug.warn('Nenhuma taxa encontrada');
            UI.showError(CONFIG.MESSAGES.NO_RATES);
          }
          UI.resetButton();
        })
        .catch(err => {
          Debug.error('Erro:', err);
          UI.showError(CONFIG.MESSAGES.ERROR);
          UI.resetButton();
        });
    },

    onInputChange: function(e) {
      const formatted = Utils.formatCEP(e.target.value);
      if (formatted.length <= CONFIG.LIMITS.CEP_LENGTH + 1) {
        e.target.value = formatted;
      }
    },

    onEnterPress: function(e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        UI.elements.button.click();
      }
    },

    setupQuantityListeners: function() {
      Debug.log('Configurando listeners de quantidade...');

      const buyMore = BuyMore.getBlock();
      if (buyMore) {
        buyMore.querySelectorAll(CONFIG.SELECTORS.BUY_MORE_RADIO).forEach(radio => {
          radio.addEventListener('change', UI.showQuantityChanged.bind(UI));
        });

        buyMore.querySelectorAll(CONFIG.SELECTORS.BUY_MORE_DYNAMIC_INPUT).forEach(input => {
          input.addEventListener('change', UI.showQuantityChanged.bind(UI));
          input.addEventListener('input', UI.showQuantityChanged.bind(UI));
        });

        Debug.log('Listeners do buy-more configurados');
      }

      const qtyInput = Quantity.getStandardInput();
      if (qtyInput) {
        qtyInput.addEventListener('change', UI.showQuantityChanged.bind(UI));
        qtyInput.addEventListener('input', UI.showQuantityChanged.bind(UI));
        Debug.log('Listener do quantity input configurado');
      }

      document.querySelectorAll(CONFIG.SELECTORS.VARIANT_SELECTORS).forEach(selector => {
        if (!DOM.isAuxiliaryElement(selector)) {
          selector.addEventListener('change', UI.showQuantityChanged.bind(UI));
        }
      });

      Debug.log('Listeners configurados com sucesso');
    }
  };

  function initialize() {
    const input = document.querySelector(CONFIG.SELECTORS.INPUT);
    const button = document.querySelector(CONFIG.SELECTORS.BUTTON);
    const message = document.querySelector(CONFIG.SELECTORS.MESSAGE);

    if (!input || !button || !message) {
      Debug.warn('Elementos não encontrados');
      return false;
    }

    UI.init(input, button, message);
    
    input.value = Storage.loadCEP();
    
    button.addEventListener('click', EventHandlers.onCalculateClick);
    input.addEventListener('input', EventHandlers.onInputChange);
    input.addEventListener('keypress', EventHandlers.onEnterPress);
    
    EventHandlers.setupQuantityListeners();

    Debug.success('Shipping Calculator inicializado');
    return true;
  }

  function retry() {
    if (!initialize()) {
      setTimeout(() => {
        if (!initialize()) {
          setTimeout(initialize, CONFIG.TIMING.INIT_RETRY);
        }
      }, CONFIG.TIMING.RETRY_DELAY);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', retry);
  } else {
    retry();
  }
})();
